// ============================================================================
// EDUSPHERE ENTERPRISE - ZUSTAND STORE CONFIGURATION
// Centralized state management with type-safe slices
// Optimized for Indian Schools (Tamil Nadu State Board)
// ============================================================================

import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';
import { User, UserRole, AcademicYear, Branch, NotificationItem, TableState } from '@/types';
import { ThemeMode } from '@/config/theme';

// Export Indian School Context
export { 
  IndianSchoolProvider, 
  useIndianSchool,
  BoardSelector,
  GradeDisplay,
  PassingStatus,
  getBoardConfig,
  getSubjectsForBoard,
  getGradeForMarks,
  getPassingMarks,
  isPassingGrade,
  calculateCGPA,
  TN_STATE_BOARD_CONFIG,
} from './IndianSchoolContext';

// ============================================================================
// AUTH STORE
// ============================================================================

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  // True once the app has asked the server (GET /auth/me, via the httpOnly
  // session cookie) whether a session actually exists. Route guards must
  // wait for this before deciding to show the login page or the app — that
  // gate is what prevents the login/dashboard flicker on a fresh page load.
  isInitialized: boolean;
  isLoading: boolean;
  error: string | null;
}

interface AuthActions {
  setUser: (user: User | null) => void;
  login: (userData: any) => void;
  logout: () => void;
  setInitialized: (initialized: boolean) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  hasPermission: (module: string, action: string) => boolean;
  hasRole: (role: UserRole) => boolean;
}

export const useAuthStore = create<AuthState & AuthActions>()(
  devtools(
    persist(
      (set, get) => ({
        // State
        user: null,
        isAuthenticated: false,
        isInitialized: false,
        isLoading: false,
        error: null,

        // Actions
        setUser: (user) =>
          set({ user, isAuthenticated: !!user, error: null }),

        // Tokens themselves live in httpOnly cookies the server sets on
        // /auth/login — there's nothing for the client to read or store here,
        // just the profile the login response returns.
        login: (userData) => {
          set({
            user: {
              id: userData.id,
              email: userData.email,
              username: userData.email?.split('@')[0] || 'user',
              firstName: userData.firstName || '',
              lastName: userData.lastName || '',
              primaryRole: userData.role || 'teacher',
              // Preserve the FULL roles array from the login response. The
              // backend returns e.g. ['teacher','student'] for accounts that
              // hold more than one role; keeping only `primaryRole` here
              // silently dropped every non-primary role, so a teacher+student
              // account never resolved its student/parent portal (the
              // role-based dashboard picks the FIRST matching role, and route
              // guards read this list). Fall back to the single requested role
              // for the 2FA/social paths that only carry `role`.
              roles: Array.isArray(userData.roles) && userData.roles.length > 0
                ? userData.roles
                : [userData.role || 'teacher'],
              permissions: userData.permissions || [],
              avatar: userData.avatar,
              isActive: true,
              branchId: localStorage.getItem('branchId') || userData.branchId || '',
              academicYearId: localStorage.getItem('academicYearId') || userData.academicYearId || '',
              // Identity anchors from the login response — student/parent
              // portal pages resolve "my own record" from these (NOT `id`,
              // which is the users.id row, not students.id/guardians.id).
              // Previously dropped here, which silently broke every
              // student/parent self-service feature reached via the real
              // login form (caught while wiring up Wave 5 homework).
              studentId: userData.studentId || undefined,
              employeeId: userData.employeeId || undefined,
              parentId: userData.parentId || undefined,
            } as User,
            isAuthenticated: true,
            isInitialized: true,
            error: null,
          });
        },

        // Local-only: clears client state. Callers that need the server-side
        // session actually revoked (refresh token + access-token blacklist)
        // must call authApi.logout() first — kept out of the store itself to
        // avoid a store → data/api → apiClient → store import cycle.
        logout: () => {
          localStorage.removeItem('branchId');
          localStorage.removeItem('academicYearId');
          set({
            user: null,
            isAuthenticated: false,
            isInitialized: true,
            error: null,
          });
        },

        setInitialized: (isInitialized) => set({ isInitialized }),

        setLoading: (isLoading) => set({ isLoading }),

        setError: (error) => set({ error }),

        hasPermission: (module, action) => {
          const { user } = get();
          if (!user) return false;
          // Check for wildcard module permission first
          const wildcardPerm = user.permissions.find((p) => p.module === '*');
          if (wildcardPerm?.actions.includes(action as never)) return true;
          // Then check specific module
          const permission = user.permissions.find((p) => p.module === module);
          return permission?.actions.includes(action as never) ?? false;
        },

        hasRole: (role) => {
          const { user } = get();
          return user?.roles.includes(role) ?? false;
        },
      }),
      {
        name: 'edusphere-auth',
        // isInitialized is intentionally NOT persisted — every fresh load
        // must re-ask the server, that's the whole point of the gate.
        partialize: (state) => ({
          user: state.user,
          isAuthenticated: state.isAuthenticated,
        }),
      }
    ),
    { name: 'AuthStore' }
  )
);

// ============================================================================
// APP CONFIG STORE
// ============================================================================

export type TableLayoutStyle = 'flow' | 'contained';

interface AppConfigState {
  themeMode: ThemeMode;
  sidebarCollapsed: boolean;
  sidebarWidth: number;
  currentAcademicYear: AcademicYear | null;
  currentBranch: Branch | null;
  academicYears: AcademicYear[];
  branches: Branch[];
  locale: string;
  dateFormat: string;
  currency: string;
  timezone: string;
  /** Global table display preference — applies to every DataTable in the app. */
  tableLayoutStyle: TableLayoutStyle;
}

interface AppConfigActions {
  setThemeMode: (mode: ThemeMode) => void;
  toggleTheme: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebar: () => void;
  setCurrentAcademicYear: (year: AcademicYear) => void;
  setCurrentBranch: (branch: Branch) => void;
  setAcademicYears: (years: AcademicYear[]) => void;
  setBranches: (branches: Branch[]) => void;
  setLocale: (locale: string) => void;
  setTableLayoutStyle: (style: TableLayoutStyle) => void;
  toggleTableLayoutStyle: () => void;
}

export const useAppConfigStore = create<AppConfigState & AppConfigActions>()(
  devtools(
    persist(
      (set, get) => ({
        // State
        themeMode: 'light',
        sidebarCollapsed: false,
        sidebarWidth: 260,
        currentAcademicYear: null,
        currentBranch: null,
        academicYears: [],
        branches: [],
        locale: 'en-IN',
        dateFormat: 'dd/MM/yyyy',
        currency: 'INR',
        timezone: 'Asia/Kolkata',
        tableLayoutStyle: 'flow',

        // Actions
        setThemeMode: (themeMode) => set({ themeMode }),

        toggleTheme: () =>
          set((state) => ({
            themeMode: state.themeMode === 'light' ? 'dark' : 'light',
          })),

        setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),

        toggleSidebar: () =>
          set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

        setCurrentAcademicYear: (currentAcademicYear) =>
          set({ currentAcademicYear }),

        setCurrentBranch: (currentBranch) => set({ currentBranch }),

        setAcademicYears: (academicYears) => set({ academicYears }),

        setBranches: (branches) => set({ branches }),

        setLocale: (locale) => set({ locale }),

        setTableLayoutStyle: (tableLayoutStyle) => set({ tableLayoutStyle }),

        toggleTableLayoutStyle: () =>
          set((state) => ({
            tableLayoutStyle: state.tableLayoutStyle === 'flow' ? 'contained' : 'flow',
          })),
      }),
      {
        name: 'edusphere-config',
        partialize: (state) => ({
          themeMode: state.themeMode,
          sidebarCollapsed: state.sidebarCollapsed,
          locale: state.locale,
          tableLayoutStyle: state.tableLayoutStyle,
        }),
      }
    ),
    { name: 'AppConfigStore' }
  )
);

// ============================================================================
// NOTIFICATION STORE
// ============================================================================

interface NotificationState {
  notifications: NotificationItem[];
  unreadCount: number;
  isLoading: boolean;
}

interface NotificationActions {
  setNotifications: (notifications: NotificationItem[]) => void;
  addNotification: (notification: NotificationItem) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
  clearAll: () => void;
}

export const useNotificationStore = create<NotificationState & NotificationActions>()(
  devtools(
    (set, get) => ({
      // State
      notifications: [],
      unreadCount: 0,
      isLoading: false,

      // Actions
      setNotifications: (notifications) =>
        set({
          notifications,
          unreadCount: notifications.filter((n) => !n.read).length,
        }),

      addNotification: (notification) =>
        set((state) => ({
          notifications: [notification, ...state.notifications],
          unreadCount: state.unreadCount + (notification.read ? 0 : 1),
        })),

      markAsRead: (id) =>
        set((state) => ({
          notifications: state.notifications.map((n) =>
            n.id === id ? { ...n, read: true } : n
          ),
          unreadCount: Math.max(0, state.unreadCount - 1),
        })),

      markAllAsRead: () =>
        set((state) => ({
          notifications: state.notifications.map((n) => ({ ...n, read: true })),
          unreadCount: 0,
        })),

      removeNotification: (id) =>
        set((state) => {
          const notification = state.notifications.find((n) => n.id === id);
          return {
            notifications: state.notifications.filter((n) => n.id !== id),
            unreadCount: notification?.read
              ? state.unreadCount
              : state.unreadCount - 1,
          };
        }),

      clearAll: () => set({ notifications: [], unreadCount: 0 }),
    }),
    { name: 'NotificationStore' }
  )
);

// ============================================================================
// UI STATE STORE (for page-level state)
// ============================================================================

interface UIState {
  commandPaletteOpen: boolean;
  searchQuery: string;
  activeModal: string | null;
  modalData: Record<string, unknown> | null;
  pageLoading: boolean;
  breadcrumbs: { label: string; path?: string }[];
}

interface UIActions {
  openCommandPalette: () => void;
  closeCommandPalette: () => void;
  toggleCommandPalette: () => void;
  setSearchQuery: (query: string) => void;
  openModal: (modalId: string, data?: Record<string, unknown>) => void;
  closeModal: () => void;
  setPageLoading: (loading: boolean) => void;
  setBreadcrumbs: (breadcrumbs: { label: string; path?: string }[]) => void;
}

export const useUIStore = create<UIState & UIActions>()(
  devtools(
    (set) => ({
      // State
      commandPaletteOpen: false,
      searchQuery: '',
      activeModal: null,
      modalData: null,
      pageLoading: false,
      breadcrumbs: [],

      // Actions
      openCommandPalette: () => set({ commandPaletteOpen: true }),
      closeCommandPalette: () => set({ commandPaletteOpen: false }),
      toggleCommandPalette: () =>
        set((state) => ({ commandPaletteOpen: !state.commandPaletteOpen })),

      setSearchQuery: (searchQuery) => set({ searchQuery }),

      openModal: (activeModal, modalData = undefined) =>
        set({ activeModal, modalData }),


      closeModal: () => set({ activeModal: null, modalData: null }),

      setPageLoading: (pageLoading) => set({ pageLoading }),

      setBreadcrumbs: (breadcrumbs) => set({ breadcrumbs }),
    }),
    { name: 'UIStore' }
  )
);

// ============================================================================
// TABLE STATE FACTORY (for data grid state management)
// ============================================================================

interface TableStoreState {
  tables: Record<string, TableState>;
}

interface TableStoreActions {
  getTableState: (tableId: string) => TableState;
  setTableState: (tableId: string, state: Partial<TableState>) => void;
  resetTableState: (tableId: string) => void;
  clearAllTables: () => void;
}

const defaultTableState: TableState = {
  pagination: { pageIndex: 0, pageSize: 25 },
  sorting: [],
  filters: [],
  globalFilter: '',
  columnVisibility: {},
  rowSelection: {},
};

export const useTableStore = create<TableStoreState & TableStoreActions>()(
  devtools(
    persist(
      (set, get) => ({
        // State
        tables: {},

        // Actions
        getTableState: (tableId) => {
          const { tables } = get();
          return tables[tableId] ?? { ...defaultTableState };
        },

        setTableState: (tableId, newState) =>
          set((state) => ({
            tables: {
              ...state.tables,
              [tableId]: {
                ...(state.tables[tableId] ?? defaultTableState),
                ...newState,
              },
            },
          })),

        resetTableState: (tableId) =>
          set((state) => ({
            tables: {
              ...state.tables,
              [tableId]: { ...defaultTableState },
            },
          })),

        clearAllTables: () => set({ tables: {} }),
      }),
      {
        name: 'edusphere-tables',
      }
    ),
    { name: 'TableStore' }
  )
);

// ============================================================================
// STUDENT MODULE STORE
// ============================================================================

interface StudentModuleState {
  selectedStudentIds: string[];
  activeFilters: {
    classId?: string;
    sectionId?: string;
    status?: string;
    category?: string;
  };
  viewMode: 'grid' | 'list';
}

interface StudentModuleActions {
  setSelectedStudents: (ids: string[]) => void;
  toggleStudentSelection: (id: string) => void;
  clearSelection: () => void;
  setFilters: (filters: StudentModuleState['activeFilters']) => void;
  setViewMode: (mode: 'grid' | 'list') => void;
}

export const useStudentModuleStore = create<StudentModuleState & StudentModuleActions>()(
  devtools(
    (set, get) => ({
      // State
      selectedStudentIds: [],
      activeFilters: {},
      viewMode: 'list',

      // Actions
      setSelectedStudents: (selectedStudentIds) => set({ selectedStudentIds }),

      toggleStudentSelection: (id) =>
        set((state) => ({
          selectedStudentIds: state.selectedStudentIds.includes(id)
            ? state.selectedStudentIds.filter((sid) => sid !== id)
            : [...state.selectedStudentIds, id],
        })),

      clearSelection: () => set({ selectedStudentIds: [] }),

      setFilters: (activeFilters) => set({ activeFilters }),

      setViewMode: (viewMode) => set({ viewMode }),
    }),
    { name: 'StudentModuleStore' }
  )
);

// ============================================================================
// FEE COLLECTION STORE
// ============================================================================

interface FeeCollectionState {
  currentStudent: {
    id: string;
    name: string;
    admissionNumber: string;
    className: string;
    sectionName: string;
  } | null;
  cartItems: {
    feeHeadId: string;
    feeHeadName: string;
    amount: number;
    discount: number;
    netAmount: number;
  }[];
  paymentMode: string;
  paymentDetails: Record<string, string>;
  totalAmount: number;
  totalDiscount: number;
  netPayable: number;
}

interface FeeCollectionActions {
  setCurrentStudent: (student: FeeCollectionState['currentStudent']) => void;
  addToCart: (item: FeeCollectionState['cartItems'][0]) => void;
  removeFromCart: (feeHeadId: string) => void;
  updateCartItem: (feeHeadId: string, updates: Partial<FeeCollectionState['cartItems'][0]>) => void;
  clearCart: () => void;
  setPaymentMode: (mode: string) => void;
  setPaymentDetails: (details: Record<string, string>) => void;
  calculateTotals: () => void;
  resetCollection: () => void;
}

export const useFeeCollectionStore = create<FeeCollectionState & FeeCollectionActions>()(
  devtools(
    (set, get) => ({
      // State
      currentStudent: null,
      cartItems: [],
      paymentMode: 'cash',
      paymentDetails: {},
      totalAmount: 0,
      totalDiscount: 0,
      netPayable: 0,

      // Actions
      setCurrentStudent: (currentStudent) => set({ currentStudent }),

      addToCart: (item) =>
        set((state) => {
          const exists = state.cartItems.find((i) => i.feeHeadId === item.feeHeadId);
          if (exists) return state;
          const newCartItems = [...state.cartItems, item];
          return {
            cartItems: newCartItems,
            ...calculateCartTotals(newCartItems),
          };
        }),

      removeFromCart: (feeHeadId) =>
        set((state) => {
          const newCartItems = state.cartItems.filter((i) => i.feeHeadId !== feeHeadId);
          return {
            cartItems: newCartItems,
            ...calculateCartTotals(newCartItems),
          };
        }),

      updateCartItem: (feeHeadId, updates) =>
        set((state) => {
          const newCartItems = state.cartItems.map((item) =>
            item.feeHeadId === feeHeadId
              ? { ...item, ...updates, netAmount: (updates.amount ?? item.amount) - (updates.discount ?? item.discount) }
              : item
          );
          return {
            cartItems: newCartItems,
            ...calculateCartTotals(newCartItems),
          };
        }),

      clearCart: () =>
        set({
          cartItems: [],
          totalAmount: 0,
          totalDiscount: 0,
          netPayable: 0,
        }),

      setPaymentMode: (paymentMode) => set({ paymentMode, paymentDetails: {} }),

      setPaymentDetails: (paymentDetails) => set({ paymentDetails }),

      calculateTotals: () =>
        set((state) => calculateCartTotals(state.cartItems)),

      resetCollection: () =>
        set({
          currentStudent: null,
          cartItems: [],
          paymentMode: 'cash',
          paymentDetails: {},
          totalAmount: 0,
          totalDiscount: 0,
          netPayable: 0,
        }),
    }),
    { name: 'FeeCollectionStore' }
  )
);

function calculateCartTotals(items: FeeCollectionState['cartItems']) {
  const totalAmount = items.reduce((sum, item) => sum + item.amount, 0);
  const totalDiscount = items.reduce((sum, item) => sum + item.discount, 0);
  return {
    totalAmount,
    totalDiscount,
    netPayable: totalAmount - totalDiscount,
  };
}

// ============================================================================
// MARKS ENTRY STORE
// ============================================================================

interface MarksEntryState {
  examId: string | null;
  classId: string | null;
  sectionId: string | null;
  subjectId: string | null;
  maxMarks: number;
  passingMarks: number;
  students: {
    id: string;
    name: string;
    rollNumber: string;
    marks: number | null;
    isAbsent: boolean;
    isExempted: boolean;
    remarks: string;
    isDirty: boolean;
  }[];
  isLocked: boolean;
  hasUnsavedChanges: boolean;
}

interface MarksEntryActions {
  setExamConfig: (config: {
    examId: string;
    classId: string;
    sectionId: string;
    subjectId: string;
    maxMarks: number;
    passingMarks: number;
  }) => void;
  setStudents: (students: MarksEntryState['students']) => void;
  updateStudentMarks: (studentId: string, marks: number | null) => void;
  toggleAbsent: (studentId: string) => void;
  toggleExempted: (studentId: string) => void;
  setRemarks: (studentId: string, remarks: string) => void;
  markAsSaved: () => void;
  setLocked: (locked: boolean) => void;
  resetMarksEntry: () => void;
}

export const useMarksEntryStore = create<MarksEntryState & MarksEntryActions>()(
  devtools(
    (set) => ({
      // State
      examId: null,
      classId: null,
      sectionId: null,
      subjectId: null,
      maxMarks: 100,
      passingMarks: 35,
      students: [],
      isLocked: false,
      hasUnsavedChanges: false,

      // Actions
      setExamConfig: ({ examId, classId, sectionId, subjectId, maxMarks, passingMarks }) =>
        set({ examId, classId, sectionId, subjectId, maxMarks, passingMarks }),

      setStudents: (students) =>
        set({ students: students.map((s) => ({ ...s, isDirty: false })) }),

      updateStudentMarks: (studentId, marks) =>
        set((state) => ({
          students: state.students.map((s) =>
            s.id === studentId
              ? { ...s, marks, isDirty: true, isAbsent: marks === null ? s.isAbsent : false }
              : s
          ),
          hasUnsavedChanges: true,
        })),

      toggleAbsent: (studentId) =>
        set((state) => ({
          students: state.students.map((s) =>
            s.id === studentId
              ? { ...s, isAbsent: !s.isAbsent, marks: !s.isAbsent ? null : s.marks, isDirty: true }
              : s
          ),
          hasUnsavedChanges: true,
        })),

      toggleExempted: (studentId) =>
        set((state) => ({
          students: state.students.map((s) =>
            s.id === studentId ? { ...s, isExempted: !s.isExempted, isDirty: true } : s
          ),
          hasUnsavedChanges: true,
        })),

      setRemarks: (studentId, remarks) =>
        set((state) => ({
          students: state.students.map((s) =>
            s.id === studentId ? { ...s, remarks, isDirty: true } : s
          ),
          hasUnsavedChanges: true,
        })),

      markAsSaved: () =>
        set((state) => ({
          students: state.students.map((s) => ({ ...s, isDirty: false })),
          hasUnsavedChanges: false,
        })),

      setLocked: (isLocked) => set({ isLocked }),

      resetMarksEntry: () =>
        set({
          examId: null,
          classId: null,
          sectionId: null,
          subjectId: null,
          maxMarks: 100,
          passingMarks: 35,
          students: [],
          isLocked: false,
          hasUnsavedChanges: false,
        }),
    }),
    { name: 'MarksEntryStore' }
  )
);
